import { useEffect } from 'react'

// Key used to store the opened thread ID in the URL
export const THREAD_PARAM_KEY = 'opened_thread'

// Handler for expanding the `Read More` overlay and scrolling to the thread
const handleThreadOpen = (
  editorElement: HTMLElement | null,
  threadId: string,
  onOpenThread: (id: string) => void
) => {
  const readMoreOverlayButton = editorElement?.querySelector<HTMLElement>(
    '.read-more-button-collapsed'
  )
  const threadElement = editorElement?.querySelector<HTMLElement>(
    `[data-thread-id="${threadId}"]`
  )

  // Expands the `Read More` overlay if it exists
  if (readMoreOverlayButton) {
    readMoreOverlayButton.click()
  }

  if (threadElement && threadId) {
    setTimeout(() => {
      threadElement.scrollIntoView({ behavior: 'instant', block: 'center' })
      onOpenThread(threadId)
    }, 500) // Slight delay to allow the page to initialize
  }
}

// Checks the URL for a thread ID and navigates to it
const checkUrlForThreadId = (
  editorRef: HTMLElement | null,
  onOpenThread: (id: string) => void
) => {
  const searchParams = new URLSearchParams(window.top?.location.search)
  const threadId = searchParams.get(THREAD_PARAM_KEY)
  if (threadId) {
    handleThreadOpen(editorRef, threadId, onOpenThread)
  }
}

/**
 * Manages thread route navigation for the collaboration text editor.
 * This hook adds a route parameter to the current URL when a thread is opened.
 * This can be used navigate directly to an opened thread. IE: `?opened_thread={threadId}`
 */
export function useThreadNavigation({
  editorRef,
  isSynced = false,
  onOpenThread,
}: {
  editorRef: React.RefObject<HTMLElement>
  isSynced?: boolean
  onOpenThread: (id: string) => void
}) {
  // Listens for popstate events to check for thread ID
  useEffect(() => {
    if (!isSynced) return

    // Checks for thread ID in URL on initial load for direct navigation
    checkUrlForThreadId(editorRef.current, onOpenThread)

    const handlePopState = () =>
      checkUrlForThreadId(editorRef.current, onOpenThread)

    window.top?.addEventListener('popstate', handlePopState)

    return () => {
      window.top?.removeEventListener('popstate', handlePopState)
    }
  }, [editorRef, isSynced, onOpenThread])
}
